import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const useAdminInactivityLogout = () => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isLoggingOut = useRef(false);

  const logout = useCallback(() => {
    // Prevent multiple logout calls
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Remove token
    localStorage.removeItem('admin_token');
    
    // Show message and redirect
    toast.info('Sie wurden wegen Inaktivität automatisch abgemeldet');
    navigate('/admin/login', { replace: true });
    
    // Reset flag after a short delay
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
  }, [navigate]);

  const resetTimer = useCallback(() => {
    // Don't reset if logging out
    if (isLoggingOut.current) return;
    
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Only set timeout if admin is logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      timeoutRef.current = setTimeout(() => {
        logout();
      }, INACTIVITY_TIMEOUT);
    }
  }, [logout]);

  useEffect(() => {
    // Check if we have a valid token on mount
    const token = localStorage.getItem('admin_token');
    if (!token) {
      return;
    }
    
    // Events that indicate user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Initial timer setup
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer]);

  // Check on visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('admin_token');
        if (token) {
          const timeSinceLastActivity = Date.now() - lastActivityRef.current;
          if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
            logout();
          } else {
            resetTimer();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [logout, resetTimer]);

  return { resetTimer };
};

export default useAdminInactivityLogout;
